import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, Pencil, Trash2, Image as ImageIcon, X as XIcon } from "lucide-react";
import FileUploader from "@/components/FileUploader";
import { useAuth } from "@/context/AuthContext";

const inputCls =
  "w-full bg-white border border-[#e5e0d8] text-[#1c1a17] rounded-sm px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-[#234333] focus:border-[#234333]";

const emptyForm = {
  gallery_type: "",
  caption: "",
  date_taken: "",
  place: "",
  notes: "",
  image_url: "",
  image_public_id: "",
};

function Field({ label, children, full }) {
  return (
    <label className={`block ${full ? "md:col-span-2" : ""}`}>
      <div className="text-[10px] tracking-[0.2em] uppercase text-[#6b655b] font-semibold mb-2">
        {label}
      </div>
      {children}
    </label>
  );
}

export default function InstituteGallery() {
  const { role } = useAuth();
  const canEdit = role === "institute_admin";

  const [types, setTypes] = useState([]);
  const [activeType, setActiveType] = useState("");
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [lightbox, setLightbox] = useState(null);

  const fetchTypes = async () => {
    try {
      const { data } = await api.get("/masters/gallery_type");
      setTypes(data);
    } catch {
      setTypes([]);
    }
  };

  const fetchPhotos = async (type) => {
    const url = type
      ? `/institute/gallery/photos?type=${encodeURIComponent(type)}`
      : "/institute/gallery/photos";
    const { data } = await api.get(url);
    setPhotos(data);
  };

  useEffect(() => {
    (async () => {
      await Promise.all([fetchTypes(), fetchPhotos("")]);
      setLoading(false);
    })();
  }, []);

  useEffect(() => {
    fetchPhotos(activeType);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeType]);

  const countsByType = useMemo(() => {
    const m = {};
    photos.forEach((p) => {
      const key = p.gallery_type || "(uncategorised)";
      m[key] = (m[key] || 0) + 1;
    });
    return m;
  }, [photos]);

  const openCreate = () => {
    setEditing(null);
    setForm({ ...emptyForm, gallery_type: activeType });
    setOpen(true);
  };

  const openEdit = (p) => {
    setEditing(p);
    setForm({ ...emptyForm, ...p, date_taken: p.date_taken || "" });
    setOpen(true);
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.image_url) {
      toast.error("Please upload a photo");
      return;
    }
    setSaving(true);
    const payload = { ...form, date_taken: form.date_taken || null };
    try {
      if (editing) {
        const { data } = await api.put(`/institute/gallery/photos/${editing.id}`, payload);
        setPhotos((arr) => arr.map((x) => (x.id === data.id ? data : x)));
        toast.success("Photo updated");
      } else {
        const { data } = await api.post("/institute/gallery/photos", payload);
        if (!activeType || data.gallery_type === activeType) {
          setPhotos((arr) => [data, ...arr]);
        } else {
          setActiveType(data.gallery_type);
        }
        toast.success("Photo added");
      }
      setOpen(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async () => {
    const p = confirmDelete;
    setConfirmDelete(null);
    try {
      await api.delete(`/institute/gallery/photos/${p.id}`);
      setPhotos((arr) => arr.filter((x) => x.id !== p.id));
      toast.success("Photo removed");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Delete failed");
    }
  };

  return (
    <div className="p-8 md:p-12 max-w-7xl mx-auto" data-testid="inst-gallery-page">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#6b655b]">Moments</div>
          <h1 className="mt-2 font-serif text-4xl text-[#1c1a17]">Institute Gallery</h1>
          <p className="mt-2 text-sm text-[#6b655b] max-w-2xl">
            Photos from events, cultural days, sports, alumni meets and other moments at your
            institute. JPG / PNG / WEBP, up to <strong>300 KB</strong> per image.
          </p>
        </div>
        {canEdit && (
          <button
            onClick={openCreate}
            data-testid="inst-gallery-add-button"
            className="inline-flex items-center gap-2 bg-[#234333] hover:bg-[#1b3326] text-white text-sm px-6 py-2.5 rounded-sm"
          >
            <Plus size={14} strokeWidth={1.5} /> Add Photo
          </button>
        )}
      </div>

      {/* Type strip */}
      <div
        className="flex gap-2 flex-wrap mb-8 border-b border-[#e5e0d8] pb-4"
        data-testid="inst-gallery-type-strip"
      >
        <button
          onClick={() => setActiveType("")}
          data-testid="inst-gallery-type-all"
          className={`text-sm px-4 py-2 rounded-sm border transition-colors ${
            activeType === ""
              ? "bg-[#234333] text-white border-[#234333]"
              : "bg-white text-[#1c1a17] border-[#e5e0d8] hover:bg-[#efece6]"
          }`}
        >
          All
        </button>
        {types.map((t) => (
          <button
            key={t.id}
            onClick={() => setActiveType(t.name)}
            data-testid={`inst-gallery-type-tab-${t.name.replace(/\s+/g, "-")}`}
            className={`text-sm px-4 py-2 rounded-sm border transition-colors ${
              activeType === t.name
                ? "bg-[#234333] text-white border-[#234333]"
                : "bg-white text-[#1c1a17] border-[#e5e0d8] hover:bg-[#efece6]"
            }`}
          >
            {t.name}
            {countsByType[t.name] ? (
              <span
                className={`ml-2 text-[11px] ${
                  activeType === t.name ? "text-white/80" : "text-[#6b655b]"
                }`}
              >
                · {countsByType[t.name]}
              </span>
            ) : null}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-[#6b655b] font-serif text-xl">Loading…</div>
      ) : photos.length === 0 ? (
        <div
          className="bg-white border border-dashed border-[#e5e0d8] rounded-sm p-16 text-center"
          data-testid="inst-gallery-empty"
        >
          <ImageIcon size={32} strokeWidth={1.5} className="mx-auto text-[#234333]" />
          <div className="mt-4 font-serif text-2xl text-[#1c1a17]">
            {activeType ? `No photos in ${activeType} yet` : "No photos yet"}
          </div>
          <div className="mt-2 text-sm text-[#6b655b]">
            {canEdit
              ? `Click "Add Photo" to upload the first one.`
              : "Your Institute Admin hasn't added any yet."}
          </div>
        </div>
      ) : (
        <div
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5"
          data-testid="inst-gallery-grid"
        >
          {photos.map((p) => (
            <div
              key={p.id}
              data-testid={`inst-gallery-card-${p.id}`}
              className="bg-white border border-[#e5e0d8] rounded-sm overflow-hidden group"
            >
              <button
                type="button"
                onClick={() => setLightbox(p)}
                className="block w-full aspect-[4/3] bg-[#f9f8f6] overflow-hidden"
              >
                <img
                  src={p.image_url}
                  alt={p.caption}
                  className="w-full h-full object-cover transition-transform group-hover:scale-105"
                  loading="lazy"
                />
              </button>
              <div className="p-4">
                <div className="font-serif text-lg text-[#1c1a17] leading-tight line-clamp-2">
                  {p.caption || <span className="text-[#6b655b] italic">Untitled</span>}
                </div>
                <div className="mt-2 text-[11px] text-[#6b655b] tracking-wide uppercase flex gap-2 flex-wrap">
                  {p.gallery_type && <span>{p.gallery_type}</span>}
                  {p.date_taken && <span>· {p.date_taken}</span>}
                  {p.place && <span>· {p.place}</span>}
                </div>
                {p.notes && (
                  <div className="mt-2 text-xs text-[#6b655b] line-clamp-2">{p.notes}</div>
                )}
                {canEdit && (
                  <div className="mt-3 flex gap-1">
                    <button
                      onClick={() => openEdit(p)}
                      data-testid={`inst-gallery-edit-${p.id}`}
                      className="inline-flex items-center gap-1 text-xs text-[#234333] hover:bg-[#efece6] px-2 py-1 rounded-sm"
                    >
                      <Pencil size={12} strokeWidth={1.5} /> Edit
                    </button>
                    <button
                      onClick={() => setConfirmDelete(p)}
                      data-testid={`inst-gallery-delete-${p.id}`}
                      className="inline-flex items-center gap-1 text-xs text-[#9e2a2b] hover:bg-[#9e2a2b]/5 px-2 py-1 rounded-sm"
                    >
                      <Trash2 size={12} strokeWidth={1.5} /> Delete
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Lightbox */}
      {lightbox && (
        <div
          className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
          onClick={() => setLightbox(null)}
          data-testid="inst-gallery-lightbox"
        >
          <button
            type="button"
            className="absolute top-5 right-5 text-white/80 hover:text-white p-2"
            onClick={() => setLightbox(null)}
          >
            <XIcon size={22} />
          </button>
          <figure className="max-w-5xl max-h-full" onClick={(e) => e.stopPropagation()}>
            <img
              src={lightbox.image_url}
              alt={lightbox.caption}
              className="max-w-full max-h-[80vh] object-contain"
            />
            <figcaption className="mt-4 text-white text-center">
              <div className="font-serif text-2xl">{lightbox.caption || "Untitled"}</div>
              <div className="mt-1 text-sm text-white/70 tracking-wide uppercase">
                {[lightbox.gallery_type, lightbox.date_taken, lightbox.place]
                  .filter(Boolean)
                  .join(" · ")}
              </div>
              {lightbox.notes && (
                <div className="mt-2 max-w-xl mx-auto text-sm text-white/80">{lightbox.notes}</div>
              )}
            </figcaption>
          </figure>
        </div>
      )}

      {/* Add/Edit dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className="bg-white border-[#e5e0d8] rounded-sm max-w-2xl max-h-[92vh] overflow-y-auto"
          data-testid="inst-gallery-dialog"
        >
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-[#1c1a17]">
              {editing ? "Edit Photo" : "Add Photo"}
            </DialogTitle>
          </DialogHeader>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 py-2">
            <Field label="Gallery Type">
              <select
                data-testid="inst-gallery-type-select"
                className={inputCls}
                value={form.gallery_type}
                onChange={(e) => set("gallery_type", e.target.value)}
              >
                <option value="">— None —</option>
                {types.map((t) => (
                  <option key={t.id} value={t.name}>
                    {t.name}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Date Taken">
              <input
                data-testid="inst-gallery-date-input"
                type="date"
                className={inputCls}
                value={form.date_taken}
                onChange={(e) => set("date_taken", e.target.value)}
              />
            </Field>
            <Field label="Caption" full>
              <input
                data-testid="inst-gallery-caption-input"
                className={inputCls}
                placeholder="e.g., Foundation Day 2024, main stage"
                value={form.caption}
                onChange={(e) => set("caption", e.target.value)}
              />
            </Field>
            <Field label="Place">
              <input
                data-testid="inst-gallery-place-input"
                className={inputCls}
                placeholder="e.g., Main Auditorium"
                value={form.place}
                onChange={(e) => set("place", e.target.value)}
              />
            </Field>
            <Field label="Other details" full>
              <textarea
                data-testid="inst-gallery-notes-input"
                className={inputCls + " min-h-[72px] resize-y"}
                placeholder="Any additional context — photographer, people in frame, etc."
                value={form.notes}
                onChange={(e) => set("notes", e.target.value)}
              />
            </Field>

            <div className="md:col-span-2 pt-2">
              <FileUploader
                kind="inst_gallery_photo"
                label="Photo"
                url={form.image_url}
                testid="inst-gallery-image"
                onChange={({ url, public_id }) =>
                  setForm((f) => ({ ...f, image_url: url, image_public_id: public_id }))
                }
              />
            </div>
          </div>

          <DialogFooter className="pt-4">
            <button
              onClick={() => setOpen(false)}
              data-testid="inst-gallery-cancel-button"
              className="bg-white hover:bg-[#f9f8f6] text-[#1c1a17] border border-[#e5e0d8] text-sm px-5 py-2 rounded-sm"
            >
              Cancel
            </button>
            <button
              onClick={save}
              disabled={saving}
              data-testid="inst-gallery-save-button"
              className="bg-[#234333] hover:bg-[#1b3326] text-white text-sm px-5 py-2 rounded-sm disabled:opacity-60"
            >
              {saving ? "Saving…" : editing ? "Update" : "Create"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirmDelete} onOpenChange={(v) => !v && setConfirmDelete(null)}>
        <AlertDialogContent className="bg-white border-[#e5e0d8] rounded-sm">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif text-2xl text-[#1c1a17]">
              Delete this photo?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[#6b655b]">
              "{confirmDelete?.caption || "Untitled"}" will be permanently removed and deleted from
              storage.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-white text-[#1c1a17] border-[#e5e0d8] rounded-sm">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={doDelete}
              data-testid="inst-gallery-delete-confirm"
              className="bg-[#9e2a2b] hover:bg-[#7d2122] text-white rounded-sm"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
